import {ProductDeserializer, UnitDefinition} from '@/services/ProductDeserializer';
import {Vue} from 'vue-property-decorator';
import {
  EssenceItemDto,
  PriceDto,
  ProductAttributeInfo,
  ProductBasePrice,
  ProductImageDto,
  ProductNetFillQuantity,
  SalesConditionInfo
} from '@eurogast/hub-client-typescript';
import {PriceDeserializer} from '@/services/PriceDeserializer';
import {EssenceDeserializer} from '@/services/EssenceDeserializer';
import {Locale, LocaleDeserializer} from '@/services/LocaleDeserializer';

export const CUSTOMER_COOKIE_NAME = 'eg-customer-id';

export abstract class ConfigService {
  /**
   * Applies global settings from given element and returns any local settings set on it. Local settings are those
   * whose keys start with "local".
   */
  public static applySettings($el: HTMLElement): any {
    const localConfig: any = {};
    const {dataset} = $el;
    Object.keys(dataset).forEach((key) => {
      const value = dataset[key];
      if (value !== 'null' && value.length) {
        if (key.startsWith('local')) {
          localConfig[key] = value;
        } else {
          this.config[key] = value;
        }
      }
    });
    return localConfig;
  }

  public static parseArray(source: string): string[] {
    if (!source || source === '[]') {
      return [];
    } else if (source.startsWith('[') && source.endsWith(']')) {
      return source.slice(1, -1).split(';;');
    }
    throw Error(`Expected array, but got ${source}`);
  }

  public static parseUnits(source: string): UnitDefinition[] {
    return ConfigService.parseArray(source).map((str) => ProductDeserializer.parseUnit(str));
  }

  public static parseImages(source: string): ProductImageDto[] {
    return ConfigService.parseArray(source).map((str) => ProductDeserializer.parseImage(str));
  }

  public static parseNetFillQuantity(source: string): ProductNetFillQuantity {
    return ProductDeserializer.parseNetFill(source);
  }

  public static parseAttributes(source: string): ProductAttributeInfo[] {
    return ConfigService.parseArray(source).map((str) => ProductDeserializer.parseAttribute(str));
  }

  public static parseSalesConditions(source: string): SalesConditionInfo[] {
    return ConfigService.parseArray(source).map((str) => ProductDeserializer.parseSalesCondition(str));
  }

  public static parseBasePrice(source: string): ProductBasePrice {
    return ProductDeserializer.parseBasePrice(source);
  }

  public static parsePrice(source: string): PriceDto {
    return PriceDeserializer.parsePrice(source);
  }

  public static parseEssence(source: string): EssenceItemDto[] {
    return EssenceDeserializer.parseEssences(source);
  }

  public static parseLocales(locales: string): Locale[] {
    return LocaleDeserializer.parseLocales(locales);
  }

  public static getHubBaseUrl(): string {
    return this.requireRaw('hubUrl');
  }

  public static getAssetBaseUrl(): string {
    return this.requireRaw('assetBaseUrl');
  }

  public static getProductDetailUrl(): string {
    return this.requireRaw('productDetailUrl');
  }

  public static hasAccessToken(): boolean {
    return this.hasProperty('token');
  }

  public static getAccessToken(): string {
    return this.requireRaw('token');
  }

  public static setAccessToken(value: string) {
    this.setRaw('token', value);
  }

  public static getCustomerIds(): string[] {
    return this.parseArray(this.requireRaw('customers'));
  }

  public static get currentCustomerId(): string {
    const idOrNull = this.currentCustomerIdOrNull;
    if (idOrNull === null) {
      throw Error('no customer ids!');
    }
    return idOrNull as string;
  }

  public static set currentCustomerId(newId: string) {
    Vue.prototype.$cookie.set(CUSTOMER_COOKIE_NAME, newId, 30 /* days */);
  }

  public static get currentCustomerIdOrNull(): string | null {
    if (!this.hasProperty('customers')) {
      return null;
    }
    const ids = this.getCustomerIds().sort();
    if (!ids.length) {
      return null;
    }
    const customerCookie = Vue.prototype.$cookie.get(CUSTOMER_COOKIE_NAME);
    if (customerCookie !== null) {
      if (ids.indexOf(customerCookie) !== -1) {
        return customerCookie;
      }
    }

    this.currentCustomerId = ids[0];
    return ids[0];
  }

  public static getTenant(): string {
    return this.requireRaw('tenant');
  }

  public static getDisplayName(): string {
    return this.requireRaw('displayName');
  }

  public static getUsername(): string {
    return this.requireRaw('username').toUpperCase();
  }

  public static isLoggedIn(): boolean {
    return this.hasProperty('username');
  }

  public static isHighlightDiscounts(): boolean {
    return this.hasProperty('discountHighlight') &&
      this.requireRaw('discountHighlight') === 'true';
  }

  public static getKeycloakAccountUrl(): string {
    return this.requireRaw('keycloakAccountUrl');
  }

  public static hasLocale(): boolean {
    return this.hasProperty('currentLocale');
  }

  public static getCurrentLocale(): string {
    return this.requireRaw('currentLocale');
  }

  private static config: any = {};

  private static getRawOr(property: string, defaultFunction: () => string): string {
    if (this.hasProperty(property)) {
      return this.config[property];
    }
    return defaultFunction();
  }

  private static hasProperty(property: string) {
    return Object.prototype.hasOwnProperty.call(this.config, property);
  }

  private static requireRaw(property: string): string {
    return this.getRawOr(property, () => {
      throw Error(`Missing data attribute: ${property}`);
    });
  }

  private static setRaw(property: string, value: string) {
    this.config[property] = value;
  }
}
